import clsx from 'clsx';
import dayjs from 'dayjs';
import { Star } from 'lucide-react';
import AvatarIcon from 'Containers/Common/AvatarIcon';
import { ReactComponent as ZbLogo } from 'assets/svg/zb-logo.svg';
import styles from './StoryKit.module.scss';

const ACCENT_TYPES = new Set([
  'soldout',
  'lastSpots',
  'noCalls',
  'walkIn',
  'spotlight',
  'afternoon',
  'evening',
  'salon',
  'appOnly',
  'bookFast',
  'style',
  'openNow',
  'newLook',
  'quality',
  'chooseChair',
  'vip',
]);

export const getHeadlineCopy = (proposalType, t) => {
  const type = proposalType || 'salon';
  const base = `home.storiesProposals.${type}`;
  const line1 = t(`${base}.line1`, { defaultValue: '' });
  if (!line1) {
    return {
      eyebrow: t('home.storiesProposals.salon.eyebrow'),
      line1: t('home.storiesProposals.salon.line1'),
      line2: t('home.storiesProposals.salon.line2'),
      accent: true,
    };
  }
  return {
    eyebrow: t(`${base}.eyebrow`),
    line1,
    line2: t(`${base}.line2`),
    accent: ACCENT_TYPES.has(type),
  };
};

const LAYOUT_BY_TYPE = {
  appOnly: 'manifesto',
  bookFast: 'manifesto',
  tapBook: 'manifesto',
  salon: 'manifesto',
  quality: 'manifesto',
  online: 'manifesto',
  soldout: 'poster',
  noCalls: 'poster',
  lastSpots: 'poster',
  available: 'edge',
  walkIn: 'edge',
  openNow: 'edge',
  morning: 'edge',
  afternoon: 'edge',
  evening: 'edge',
  freshCut: 'classic',
  style: 'classic',
  newLook: 'classic',
  spotlight: 'spotlight',
  chooseChair: 'spotlight',
  vip: 'spotlight',
};

const StoryShell = ({ captureRef, typeClass, layoutClass, square, children }) => (
  <div
    ref={captureRef}
    className={clsx(styles.story_card, typeClass, layoutClass, {
      [styles.story_card_square]: square,
    })}
  >
    {children}
  </div>
);

// Nel formato Post (1:1) l'altezza è ridotta: avatar più piccolo per non far uscire il footer.
const StoryTop = ({ icon, name, eyebrow, avatarSize, square = false, manifesto = false }) => (
  <div className={styles.story_top}>
    <div className={styles.avatar_wrap}>
      <AvatarIcon icon={icon} name={name} size={avatarSize || (square ? '40px' : '56px')} />
    </div>
    {manifesto ? (
      <div className={styles.story_top_text}>
        <p className={styles.story_name}>{name}</p>
        {eyebrow ? <p className={styles.story_eyebrow}>{eyebrow}</p> : null}
      </div>
    ) : (
      <>
        <p className={styles.story_name}>{name}</p>
        {eyebrow ? <p className={styles.story_eyebrow}>{eyebrow}</p> : null}
      </>
    )}
  </div>
);

const BrandFooter = ({ t }) => (
  <div className={styles.story_brand}>
    <span className={styles.book_with}>{t('home.storiesProposals.bookWith')}</span>
    <ZbLogo className={styles.brand_logo} aria-hidden />
  </div>
);

/** Tipi "team": avatar grande al centro. */
const BIG_AVATAR_TYPES = new Set(['spotlight', 'chooseChair', 'vip']);

/** Card headline con layout variabile per tipo. */
export const HeadlineStoryCard = ({
  captureRef,
  salon,
  workerName,
  workerIcon,
  proposalType,
  layout: layoutProp,
  square = false,
  t,
}) => {
  const type = proposalType || 'salon';
  const copy = getHeadlineCopy(type, t);
  const titleName = workerName || salon?.name || salon?.nome_salone || '';
  const avatarIcon = workerIcon || salon?.icon;
  const layout = layoutProp || LAYOUT_BY_TYPE[type] || 'classic';
  const bigAvatar = BIG_AVATAR_TYPES.has(type) || layout === 'spotlight';
  const layoutClass = styles[`layout_${layout}`];

  return (
    <StoryShell
      captureRef={captureRef}
      typeClass={styles[`type_${type}`]}
      layoutClass={layoutClass}
      square={square}
    >
      <StoryTop
        icon={avatarIcon}
        name={titleName}
        eyebrow={copy.eyebrow}
        avatarSize={
          bigAvatar && !square
            ? '96px'
            : layout === 'manifesto'
              ? square
                ? '32px'
                : '40px'
              : square
                ? '40px'
                : '56px'
        }
        square={square}
        manifesto={layout === 'manifesto'}
      />
      <div className={styles.story_headline}>
        {layout === 'poster' ? <span className={styles.poster_rule} aria-hidden /> : null}
        <span className={styles.headline_line}>{copy.line1}</span>
        <span
          className={clsx(styles.headline_line, {
            [styles.headline_accent]: copy.accent,
          })}
        >
          {copy.line2}
        </span>
        {layout === 'manifesto' ? <span className={styles.manifesto_bar} aria-hidden /> : null}
        {layout === 'poster' ? <span className={styles.poster_rule} aria-hidden /> : null}
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

/** Card disponibilità con slot liberi reali — come quella generata dal calendario. */
export const SlotsStoryCard = ({
  captureRef,
  workerName,
  workerIcon,
  date,
  slots = [],
  square = false,
  t,
}) => {
  const sliced = (slots || []).slice(0, square ? 4 : 6).map((el) => ({
    oraInizio: el.oraInizio?.split(':').slice(0, 2).join(':'),
    oraFine: el.oraFine?.split(':').slice(0, 2).join(':'),
  }));
  const isToday = dayjs().isSame(dayjs(date), 'day');

  return (
    <StoryShell captureRef={captureRef} typeClass={styles.type_slots} square={square}>
      <StoryTop
        icon={workerIcon}
        name={workerName}
        avatarSize={square ? '36px' : '48px'}
        square={square}
      />
      <div className={styles.story_headline}>
        <span className={styles.slots_headline}>{t('advertising.slots.title')}</span>
        <span className={styles.slots_day}>
          {isToday
            ? t('scheduler.workerSoldOutModal.today')
            : dayjs(date).format('DD MMMM')}
        </span>
        <div className={styles.slots_grid}>
          {sliced.map((item, index) => (
            <div key={index} className={styles.slot_card}>
              <span>
                {item.oraInizio} - {item.oraFine}
              </span>
            </div>
          ))}
        </div>
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

/** Frasi divertenti: layout tipografico, virgoletta gold e testo a sinistra. */
export const FunnyStoryCard = ({ captureRef, salon, phrase, square = false, t }) => {
  const titleName = salon?.name || salon?.nome_salone || '';
  return (
    <StoryShell captureRef={captureRef} typeClass={styles.type_funny} square={square}>
      <div className={styles.funny_header}>
        <div className={styles.avatar_wrap}>
          <AvatarIcon icon={salon?.icon} name={titleName} size="32px" />
        </div>
        <span className={styles.funny_name}>{titleName}</span>
      </div>
      <div className={styles.funny_body}>
        <span className={styles.funny_quote_mark} aria-hidden>
          &ldquo;
        </span>
        <span className={styles.funny_phrase}>{phrase}</span>
        <span className={styles.funny_eyebrow}>{t('advertising.funny.eyebrow')}</span>
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

export const ReviewStoryCard = ({ captureRef, salon, review, square = false, t }) => {
  const titleName = salon?.name || salon?.nome_salone || '';
  const author =
    [review?.nome, review?.cognome].filter(Boolean).join(' ') ||
    t('advertising.reviews.anonymous');
  const rating = Math.round(Number(review?.rating_average) || 0);
  const text = review?.testo || '';

  return (
    <StoryShell captureRef={captureRef} typeClass={styles.type_review} square={square}>
      <StoryTop
        icon={salon?.icon}
        name={titleName}
        eyebrow={t('advertising.reviews.eyebrow')}
        square={square}
      />
      <div className={styles.story_headline}>
        <div className={styles.stars_row} aria-hidden>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={18}
              strokeWidth={0}
              fill={i < rating ? 'currentColor' : 'rgba(255,255,255,0.22)'}
            />
          ))}
        </div>
        {text ? <p className={styles.review_quote}>&ldquo;{text}&rdquo;</p> : null}
        <p className={styles.review_author}>{author}</p>
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

export const QrStoryCard = ({ captureRef, salon, square = false, t }) => {
  const titleName = salon?.name || salon?.nome_salone || '';
  const username = salon?.username || salon?.name || 'salone';
  const salonLink = `https://application.zetabarber.it/barber/${encodeURIComponent(username)}`;
  const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(salonLink)}`;

  return (
    <StoryShell captureRef={captureRef} typeClass={styles.type_qr} square={square}>
      <StoryTop
        icon={salon?.icon}
        name={titleName}
        eyebrow={t('advertising.qr.eyebrow')}
        square={square}
      />
      <div className={styles.qr_wrap}>
        <img className={styles.qr_img} src={qrImg} alt="" crossOrigin="anonymous" />
        <p className={styles.qr_hint}>{t('advertising.qr.hint')}</p>
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

export const PromoStoryCard = ({
  captureRef,
  salon,
  title,
  discount,
  validity,
  square = false,
  t,
}) => {
  const titleName = salon?.name || salon?.nome_salone || '';
  return (
    <StoryShell captureRef={captureRef} typeClass={styles.type_promo} square={square}>
      <div className={styles.story_top}>
        <div className={styles.avatar_wrap}>
          <AvatarIcon icon={salon?.icon} name={titleName} size={square ? '40px' : '56px'} />
        </div>
        <p className={styles.story_name}>{titleName}</p>
        <span className={styles.promo_badge}>{t('advertising.promo.badge')}</span>
      </div>
      <div className={styles.story_headline}>
        {discount ? <span className={styles.promo_discount}>{discount}</span> : null}
        <span className={styles.headline_line}>{title}</span>
        {validity ? <p className={styles.promo_validity}>{validity}</p> : null}
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};

export const SeasonalStoryCard = ({ captureRef, salon, seasonalId, square = false, t }) => {
  const titleName = salon?.name || salon?.nome_salone || '';
  const typeClass = styles[`type_seasonal_${seasonalId}`] || styles.type_promo;

  return (
    <StoryShell captureRef={captureRef} typeClass={typeClass} square={square}>
      <div className={styles.seasonal_frame} aria-hidden />
      <StoryTop
        icon={salon?.icon}
        name={titleName}
        eyebrow={`✦ ${t(`advertising.seasonal.${seasonalId}.eyebrow`)} ✦`}
        square={square}
      />
      <div className={styles.story_headline}>
        <span className={styles.headline_line}>
          {t(`advertising.seasonal.${seasonalId}.line1`)}
        </span>
        <span className={clsx(styles.headline_line, styles.headline_accent)}>
          {t(`advertising.seasonal.${seasonalId}.line2`)}
        </span>
      </div>
      <BrandFooter t={t} />
    </StoryShell>
  );
};
